/* eslint-disable react-hooks/exhaustive-deps */
import DeleteModal from "components/DeleteModal";
import DocumentViewerModal from "components/DocumentViewer";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import {
  RefreshCw,
  CheckCircle,
  Eye,
  X,
  AlertTriangle,
  Check,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  fetchIncidentByMobileById,
  updateIncidentStatus,
} from "services/incidentService";
import { DocumentSchema, FILE_URL } from "types";
import { DateView, errorHandlerOnForm } from "utils/helper";

// Custom Confirmation Modal Component
import { TFunction } from "i18next";

const CloseConfirmationModal: React.FC<{
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  t: TFunction;
  incidentDetails?: {
    complainantName?: string;
    incidentAgainst?: string;
    dateOfOffense?: Date;
  };
}> = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText,
  cancelText,
  loading = false,
  t,
  incidentDetails,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onCancel}
          aria-hidden="true"
        ></div>

        <span
          className="hidden sm:inline-block sm:h-screen sm:align-middle"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  {title || t("confirm_action")}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {message || t("are_you_sure_proceed")}
                  </p>

                  {incidentDetails && (
                    <div className="mt-4 rounded-md bg-gray-50 p-3">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {t("incident_summary")}:
                      </h4>
                      <div className="space-y-1 text-sm">
                        {incidentDetails.complainantName && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t("complainant")}:
                            </span>
                            <span className="font-medium text-gray-900">
                              {incidentDetails.complainantName}
                            </span>
                          </div>
                        )}
                        {incidentDetails.incidentAgainst && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t("incident_against")}:
                            </span>
                            <span className="font-medium text-gray-900">
                              {incidentDetails.incidentAgainst}
                            </span>
                          </div>
                        )}
                        {incidentDetails.dateOfOffense && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t("date_of_offense")}:
                            </span>
                            <span className="font-medium text-gray-900">
                              {DateView(incidentDetails.dateOfOffense)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex items-start space-x-2 rounded-md border border-yellow-200 bg-yellow-50 p-3">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-700">
                      {t("close_warning_note")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("closing")}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {confirmText || t("confirm")}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-70 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
            >
              <X className="h-4 w-4 mr-2" />
              {cancelText || t("cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reviewed Confirmation Modal (for marking as reviewed)
const ReviewedConfirmationModal: React.FC<{
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  t: TFunction;
  loading?: boolean;
}> = ({ isOpen, onConfirm, onCancel, t, loading = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onCancel}
          aria-hidden="true"
        ></div>

        <span
          className="hidden sm:inline-block sm:h-screen sm:align-middle"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <Eye className="h-6 w-6 text-blue-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  {t("mark_as_reviewed")}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {t("mark_reviewed_description")}
                  </p>

                  <div className="mt-3 flex items-start space-x-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                    <Eye className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-blue-700">
                        {t("what_happens_next")}
                      </p>
                      <ul className="text-xs text-blue-600 mt-1 space-y-1 list-disc list-inside">
                        <li>{t("incident_marked_reviewed")}</li>
                        <li>{t("further_investigation_initiated")}</li>
                        <li>{t("can_mark_closed_later")}</li>
                        <li>{t("status_history_updated")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("processing")}
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  {t("mark_as_reviewed")}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-70 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
            >
              <X className="h-4 w-4 mr-2" />
              {t("cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced status chips with better styling
const getIncidentStatusChip = (status?: string) => {
  const baseClasses =
    "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border";

  switch (status?.toLowerCase()) {
    case "pending":
      return `${baseClasses} bg-amber-50 text-amber-800 border-amber-200`;
    case "reviewed":
      return `${baseClasses} bg-blue-50 text-blue-800 border-blue-200`;
    case "closed":
      return `${baseClasses} bg-green-50 text-green-800 border-green-200`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800 border-gray-300`;
  }
};

// Enhanced Chevron Component
const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
      open ? "rotate-180" : ""
    }`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

// Reusable Info Card Component
const InfoCard: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badgeCount?: number;
  className?: string;
}> = ({ title, children, defaultOpen = true, badgeCount, className = "" }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${className}`}
    >
      <button
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {badgeCount !== undefined && (
            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
              {badgeCount}
            </span>
          )}
        </div>
        <Chevron open={isOpen} />
      </button>
      {isOpen && <div className="px-6 pb-4">{children}</div>}
    </div>
  );
};

// Reusable Info Row Component
const InfoRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className = "" }) => (
  <div
    className={`flex justify-between items-start py-3 border-b border-gray-100 last:border-b-0 ${className}`}
  >
    <dt className="text-sm font-medium text-gray-600 flex-shrink-0 pr-4">
      {label}
    </dt>
    <dd className="text-sm text-gray-900 text-right font-medium">{value}</dd>
  </div>
);

// Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "danger" | "outline" | "success";
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  t: TFunction;
}> = ({
  onClick,
  children,
  variant = "primary",
  className = "",
  disabled = false,
  loading = false,
  t,
}) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    danger: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
    success: "bg-green-600 text-white hover:bg-green-700 shadow-sm",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin h-4 w-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {t("processing")}
        </>
      ) : (
        children
      )}
    </button>
  );
};

const IncidentByMobileDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [incident, setIncident] = useState<IncidentByMobile>();
  const [loading, setLoading] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showReviewedConfirm, setShowReviewedConfirm] = useState(false);

  const fetchData = () => {
    if (!id) return;
    setLoading(true);
    fetchIncidentByMobileById(id)
      .then((data) => {
        setIncident(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_incident_details"),
          "error",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!id || !incident) return;

    setIsUpdatingStatus(true);
    try {
      await updateIncidentStatus(id, newStatus);
      showTooltip(
        t("incident_marked_as_status", { status: newStatus.toLowerCase() }),
        "success",
      );
      setIncident({
        ...incident,
        status: newStatus,
        updated_at: new Date(),
      });
    } catch (error: any) {
      showTooltip(
        errorHandlerOnForm(error) || t("error_updating_incident_status"),
        "error",
      );
    } finally {
      setIsUpdatingStatus(false);
      setShowCloseConfirm(false);
      setShowReviewedConfirm(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const currentStatus = incident?.status?.toLowerCase() || "";

  const renderHeader = (t: any) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
            <span className="text-2xl">📱</span>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {t("mobile_incident_report")}
              </h1>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 rounded-md hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={isRefreshing ? t("refreshing") : t("refresh")}
              >
                <RefreshCw
                  className={`w-5 h-5 text-gray-600 ${
                    isRefreshing ? "animate-spin" : ""
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-1">
              {incident?.status && (
                <span className={`${getIncidentStatusChip(incident.status)}`}>
                  {incident.status}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {(currentStatus === "pending" || currentStatus === "reviewed") && (
            <>
              <ActionButton
                onClick={() => setShowReviewedConfirm(true)}
                variant="primary"
                loading={isUpdatingStatus}
                t={t}
              >
                <Eye className="w-4 h-4" />
                {t("mark_as_reviewed")}
              </ActionButton>
              <ActionButton
                onClick={() => setShowCloseConfirm(true)}
                variant="success"
                loading={isUpdatingStatus}
                t={t}
              >
                <CheckCircle className="w-4 h-4" />
                {t("mark_as_closed")}
              </ActionButton>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        {renderHeader(t)}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <InfoCard title={t("incident_details")} defaultOpen={true}>
              <div className="space-y-1">
                {[
                  { label: t("status"), value: incident?.status },
                  {
                    label: t("complainant_name"),
                    value: incident?.complainant_name,
                  },
                  {
                    label: t("complainant_phone"),
                    value: incident?.complainant_phone,
                  },
                  {
                    label: t("complainant_email"),
                    value: incident?.complainant_email,
                  },
                  {
                    label: t("incident_against"),
                    value: incident?.incident_against,
                  },
                  {
                    label: t("accused_gender"),
                    value: incident?.incident_against_geneder,
                  },
                  {
                    label: t("date_of_offense"),
                    value: DateView(incident?.date_of_offense),
                  },
                  {
                    label: t("report_date"),
                    value: DateView(incident?.created_at),
                  },
                  {
                    label: t("last_updated"),
                    value: DateView(incident?.updated_at),
                  },
                ].map(
                  (item, idx) =>
                    item.value && (
                      <InfoRow
                        key={idx}
                        label={item.label}
                        value={item.value}
                      />
                    ),
                )}
              </div>
            </InfoCard>

            <InfoCard title={t("location_information")}>
              <div className="space-y-1">
                {[
                  { label: t("region"), value: incident?.region },
                  { label: t("city_zone"), value: incident?.city_zone },
                  { label: t("kebele_woreda"), value: incident?.kebele_woreda },
                ].map(
                  (item, idx) =>
                    item.value && (
                      <InfoRow
                        key={idx}
                        label={item.label}
                        value={item.value}
                      />
                    ),
                )}
              </div>
            </InfoCard>

            {incident?.offence_description && (
              <InfoCard title={t("offence_description")}>
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {incident.offence_description}
                    </p>
                  </div>
                </div>
              </InfoCard>
            )}
          </div>

          <div className="space-y-6">
            <InfoCard title={t("attached_documents")}>
              <div className="space-y-3">
                <DocumentViewerModal
                  documents={(incident?.uploaded_files || []) as any}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.WITNESS_PROTECTION}`}
                  layout="list"
                  hideEmptyFile={true}
                />
                {incident?.uploaded_files?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-gray-500">
                      {t("no_documents_attached")}
                    </p>
                  </div>
                )}
              </div>
            </InfoCard>

            <InfoCard title={t("activity_timeline")}>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex flex-col items-center mr-4">
                    <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                    <div className="w-0.5 h-8 bg-gray-300 mt-1"></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t("incident_reported")}
                    </p>
                    <p className="text-xs text-gray-500">
                      {DateView(incident?.created_at)}
                    </p>
                  </div>
                </div>
                {incident?.updated_at &&
                  incident?.updated_at !== incident?.created_at && (
                    <div className="flex items-start">
                      <div className="flex flex-col items-center mr-4">
                        <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                        <div className="w-0.5 h-8 bg-gray-300 mt-1"></div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {t("last_updated")}
                        </p>
                        <p className="text-xs text-gray-500">
                          {DateView(incident?.updated_at)}
                        </p>
                      </div>
                    </div>
                  )}
                <div className="flex items-start">
                  <div className="flex flex-col items-center mr-4">
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t("current_status")}
                    </p>
                    <p className="text-xs text-gray-500">
                      {incident?.status || t("pending")}
                    </p>
                  </div>
                </div>
              </div>
            </InfoCard>
          </div>
        </div>

        {openDelete && (
          <DeleteModal
            isOpen={openDelete}
            onConfirm={() => {
              console.log("Delete confirmed");
              setOpenDelete(false);
            }}
            onCancel={() => {
              setOpenDelete(false);
            }}
            title={t("delete_incident")}
            message={t("delete_incident_confirmation")}
          />
        )}

        <ReviewedConfirmationModal
          isOpen={showReviewedConfirm}
          onConfirm={() => handleUpdateStatus("Reviewed")}
          onCancel={() => setShowReviewedConfirm(false)}
          t={t}
          loading={isUpdatingStatus}
        />

        <CloseConfirmationModal
          isOpen={showCloseConfirm}
          onConfirm={() => handleUpdateStatus("Closed")}
          onCancel={() => setShowCloseConfirm(false)}
          title={t("mark_as_closed")}
          message={t("mark_closed_confirmation")}
          confirmText={t("yes_mark_as_closed")}
          cancelText={t("cancel")}
          t={t}
          loading={isUpdatingStatus}
          incidentDetails={{
            complainantName: incident?.complainant_name,
            incidentAgainst: incident?.incident_against,
            dateOfOffense: incident?.date_of_offense,
          }}
        />
      </div>
    </div>
  );
};

export default IncidentByMobileDetail;

export interface IncidentByMobile {
  id: number;
  uploaded_files: DocumentSchema[];
  complainant_name?: string;
  complainant_phone?: string;
  complainant_email?: string;
  incident_against?: string;
  incident_against_geneder?: string;
  date_of_offense?: Date;
  offence_description?: string;
  region?: string;
  city_zone?: string;
  kebele_woreda?: string;
  status?: string;
  created_at?: Date;
  updated_at?: Date;
}
